import { useState, useEffect } from "react";

// Third Party
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

// UI
import {
	Table,
	TableBody,
	TableHead,
	TableHeader,
	TableRow,
	TableCell,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
	Breadcrumb,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
	Card,
	CardHeader,
	CardTitle,
	CardDescription,
	CardContent,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import DinamixSkeletonTable from "@/components/skeleton/DinamixTableSkeleton";
import { Spinner } from "@/components/ui/spinner";
import { useToast } from "@/components/ui/use-toast";
import { FileUpload } from "@/components/FileUpload";

// Icons
import { ArrowLeft, FileUp, Save } from "lucide-react";

// Service
import { getToEdit, putPlan } from "@/services/action-plan";

function formatNumberWithCommas(value: string | number) {
	const stringValue = String(value || "");
	const isNegative = stringValue.startsWith("(") && stringValue.endsWith(")");
	const numericValue = isNegative ? stringValue.slice(1, -1) : stringValue;
	const formattedValue = numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
	return isNegative ? `(${formattedValue})` : formattedValue;
}

function parseParenthesisNumber(strValue: string | number) {
	const stringValue = String(strValue).replace(/,/g, "");
	const isNegative = stringValue.startsWith("(") && stringValue.endsWith(")");
	const numericValue = isNegative ? stringValue.slice(1, -1) : stringValue;
	return isNegative ? -Number(numericValue) : Number(numericValue);
}

export const Route = createFileRoute(
	"/_layout-private/report/action-plan/edit",
)({
	component: RouteComponent,
});

function RouteComponent() {
	const queryClient = useQueryClient();
	const { toast } = useToast();
	const navigate = useNavigate();
	const id = JSON.parse(localStorage.getItem("id") || "null");
	const year = JSON.parse(localStorage.getItem("year") || "null");
	const [formData, setFormData] = useState({
		planName: "",
		yearly: 0,
		companyId: id,
		description: "",
	});
	const [fileName, setFileName] = useState("");
	const [finPlanDetails, setFinPlanDetails] = useState<any[]>([]);

	// Query
	const {
		isLoading,
		isError,
		data: editData,
		error,
	} = useQuery({
		queryKey: ["actionPlanDetails", id],
		queryFn: () => getToEdit(id, year),
		enabled: !!id,
	});

	useEffect(() => {
		if (editData?.data) {
			setFormData({
				...formData,
				planName: editData?.data?.planName || "",
				yearly: editData?.data?.yearly || 0,
				description: editData?.data?.description || "",
			});

			// Transform negative numbers to parentheses format before setting state
			const formattedDetails = editData?.data?.finPlanDetails.map(
				(detail: any) => ({
					...detail,
					amount:
						detail.amount < 0 ? `(${Math.abs(detail.amount)})` : detail.amount,
				}),
			);

			setFinPlanDetails(formattedDetails || []);
			setFileName(editData?.data?.docFile || "");
		}
	}, [editData?.data]);

	const mutation = useMutation({
		mutationFn: async (data: any) => {
			await putPlan(data);
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["actionPlan"] });
			toast({
				title: "ແກ້ໄຂສຳເລັດ",
				description: "ແກ້ໄຂແຜນດຳເນີນງານສຳເລັດແລ້ວ",
				variant: "success",
			});
			navigate({ to: "/report/action-plan", search: { page: 1, startYear: 0, endYear: 0 } });
		},
		onError: (error: any) => {
			toast({
				title: "ຫຼົ້ມເຫຼວ",
				description: "ກະລຸນາກວດສອບຂໍ້ມູນແລ້ວລອງອີກຄັ້ງ.",
				variant: "destructive",
			});
		},
	});

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();

		if (!fileName) {
			toast({
				title: "ຫຼົ້ມເຫຼວ",
				description: "ກະລຸນາອັບໂຫຼດເອກະສານກ່ອນບັນທຶກ",
				variant: "destructive",
			});

			return;
		}

		const companyId = JSON.parse(localStorage.getItem("companyId") || "");
		const data = {
			companyId: companyId || "",
			yearly: formData.yearly,
			planName: formData.planName,
			finPlanDetails: finPlanDetails?.map((item) => ({
				...item,
				amount: parseParenthesisNumber(item.amount),
			})),
			docFile: fileName,
			description: formData.description,
		};

		console.log(data, "Payload");

		mutation.mutate(data);
	};

	// Update handleNumericChange function
	function handleNumericChange({
		e,
		id,
		fieldName,
		data,
		setData,
	}: {
		e: React.ChangeEvent<HTMLInputElement>;
		id: string;
		fieldName: "amount";
		data: any[];
		setData: React.Dispatch<React.SetStateAction<any[]>>;
	}) {
		const userInput = e.target.value;

		// Allow digits, parentheses, and decimal point
		const cleaned = userInput.replace(/[^\d().]/g, "");

		// Handle the case where we have both decimal and parentheses
		if (cleaned.includes(".")) {
			const parts = cleaned.split(".");
			const hasClosingParen = parts[parts.length - 1].endsWith(")");

			// If there's a decimal part, limit it to 2 places
			if (parts.length > 1) {
				let decimalPart = parts[1];
				// Remove closing parenthesis if it exists
				if (hasClosingParen) {
					decimalPart = decimalPart.slice(0, -1);
				}
				// Limit decimal places to 2
				decimalPart = decimalPart.slice(0, 2);
				// Add back closing parenthesis if it existed
				if (hasClosingParen) {
					decimalPart += ")";
				}

				const newValue = `${parts[0]}.${decimalPart}`;

				// Update the specific item in data array
				const updatedData = data.map((item) => {
					if (item.id === id) {
						return {
							...item,
							[fieldName]: newValue,
						};
					}
					return item;
				});

				setData(updatedData);
				return;
			}
		}

		// If no decimal point, just update as is
		const updatedData = data.map((item) => {
			if (item.id === id) {
				return {
					...item,
					[fieldName]: cleaned,
				};
			}
			return item;
		});

		setData(updatedData);
	}

	if (isLoading) {
		return <DinamixSkeletonTable />;
	}

	if (isError) {
		return <p>Error: {error.message}</p>;
	}

	return (
		<>
			<Breadcrumb>
				<BreadcrumbList>
					<BreadcrumbItem>
						<BreadcrumbLink>
							<Link replace to={"/report/action-plan"} search={{ page: 1, startYear: 0, endYear: 0 }}>
								ລາຍງານແຜນດຳເນີນງານ
							</Link>
						</BreadcrumbLink>
					</BreadcrumbItem>
					<BreadcrumbSeparator />
					<BreadcrumbItem>
						<BreadcrumbPage>ແກ້ໄຂຖານະການເງິນ - ໜີ້ສິນ</BreadcrumbPage>
					</BreadcrumbItem>
				</BreadcrumbList>
			</Breadcrumb>
			<Card className="mt-8">
				<CardHeader>
					<button
						type="button"
						onClick={() =>
							navigate({
								to: "/report/action-plan",
								search: { page: 1, startYear: 0, endYear: 0 },
							})
						}
						className="flex gap-1 items-center mb-4"
						aria-label="Go back to tax obligations report"
					>
						<ArrowLeft className="w-5 h-5" />
						ກັບຄືນ
					</button>
					<CardTitle>ແກ້ໄຂແຜນດຳເນີນງານ</CardTitle>
					<CardDescription>ຟອມການແກ້ໄຂແຜນດຳເນີນງານ</CardDescription>
				</CardHeader>
				<CardContent>
					<form onSubmit={handleSubmit}>
						{/* Plan Name and Year Input */}
						<div className="flex flex-col gap-2 mb-4">
							<Label htmlFor="planName">
								ຊື່ເອກະສານ <span className="text-red-500">*</span>
							</Label>
							<Input
								id="planName"
								name="planName"
								type="text"
								onChange={(e) =>
									setFormData({ ...formData, planName: e.target.value })
								}
								value={formData.planName}
								className="w-[200px]"
								required
								placeholder="ກະລຸນາປ້ອນຊື່ແຜນ..."
							/>
						</div>

						<div className="flex flex-col gap-2 mb-4">
							<Label htmlFor="planName">
								ປີ <span className="text-red-500">*</span>
							</Label>
							<Input
								id="yearly"
								name="yearly"
								type="number"
								onChange={(e) =>
									setFormData({ ...formData, yearly: Number(e.target.value) })
								}
								value={formData.yearly}
								className="w-[200px]"
								placeholder="ກະລຸນາປ້ອນປີ"
							/>
						</div>

						{/* Income and Expense Tables */}
						<Table className="border">
							<TableHeader>
								<TableRow>
									<TableHead className="py-5 text-center text-black bg-gray-100 border border-gray-300">
										ເນື້ອໃນ
									</TableHead>
									<TableHead className="py-5 text-center text-black bg-gray-100 rounded-tr-lg border border-gray-300">
										ແຜນການ
									</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{/* Income Table */}
								<TableRow className="bg-[#216C4C33]">
									<TableCell colSpan={2} className="font-bold">
										I. ລາຍຮັບ
									</TableCell>
								</TableRow>
								{finPlanDetails
									.filter((item) => item.financialType === "INCOME")
									.map((item, index) => (
										<TableRow key={index}>
											<TableCell>
												{/* detailName is displayed but not editable */}
												{item.detailName}
											</TableCell>
											<TableCell>
												{/* Only amount is editable */}
												<Input
													value={formatNumberWithCommas(item.amount)}
													onChange={(e) =>
														handleNumericChange({
															e,
															id: item.id,
															fieldName: "amount",
															data: finPlanDetails,
															setData: setFinPlanDetails,
														})
													}
													className="text-right"
												/>
											</TableCell>
										</TableRow>
									))}

								{/* Expense Table */}
								<TableRow className="bg-[#216C4C33]">
									<TableCell colSpan={2} className="font-bold">
										II. ລາຍຈ່າຍ
									</TableCell>
								</TableRow>
								{finPlanDetails
									.filter((item) => item.financialType === "EXPENSE")
									.map((item, index) => (
										<TableRow key={index}>
											<TableCell>
												{/* detailName is displayed but not editable */}
												{item.detailName}
											</TableCell>
											<TableCell>
												{/* Only amount is editable */}
												<Input
													value={formatNumberWithCommas(item.amount)}
													onChange={(e) =>
														handleNumericChange({
															e,
															id: item.id,
															fieldName: "amount",
															data: finPlanDetails,
															setData: setFinPlanDetails,
														})
													}
													className="text-right"
												/>
											</TableCell>
										</TableRow>
									))}

								<TableRow className="bg-[#216C4C33]">
									<TableCell className="font-bold">
										III. ຜົນໄດ້ຮັບກ່ອນອາກອນ
									</TableCell>
									<TableCell className="text-right">
										{formatNumberWithCommas(
											finPlanDetails
												.filter((item) => item.financialType === "INCOME")
												.reduce(
													(sum, row) =>
														sum + parseParenthesisNumber(row.amount),
													0,
												) -
											finPlanDetails
												.filter((item) => item.financialType === "EXPENSE")
												.reduce(
													(sum, row) =>
														sum + parseParenthesisNumber(row.amount),
													0,
												),
										)}
									</TableCell>
								</TableRow>
								{finPlanDetails
									.filter((item) => item.financialType === "PROFIT")
									.map((item, index) => (
										<TableRow key={index}>
											<TableCell>
												{/* detailName is displayed but not editable */}
												{item.detailName}
											</TableCell>
											<TableCell>
												{/* Only amount is editable */}
												<Input
													value={formatNumberWithCommas(item.amount)}
													onChange={(e) =>
														handleNumericChange({
															e,
															id: item.id,
															fieldName: "amount",
															data: finPlanDetails,
															setData: setFinPlanDetails,
														})
													}
													className="text-right"
												/>
											</TableCell>
										</TableRow>
									))}
							</TableBody>
						</Table>

						{/* File Upload */}
						<div className="flex gap-x-1 items-center mt-4">
							<FileUpload
								currentFile={fileName}
								onFileUpload={(value) => setFileName(value)}
							/>
						</div>

						{/* Description */}
						<div className="my-4 w-full">
							<p>ລາຍລະອຽດເພີ່ມເຕີມ</p>
							<Textarea
								id="description"
								name="description"
								rows={4}
								className="w-full border"
								placeholder="ປ້ອນຂໍ້ມູນ..."
								value={formData.description}
								onChange={(e) =>
									setFormData({ ...formData, description: e.target.value })
								}
							/>
						</div>

						{/* Action Buttons */}
						<div className="flex gap-x-3 justify-end mt-2 w-full">
							<Button
								variant={"outline"}
								type="button"
								onClick={() => navigate({ to: "/report/action-plan", search: { page: 1, startYear: 0, endYear: 0 } })}
							>
								ຍົກເລີກ
							</Button>
							<Button disabled={mutation.isPending} type="submit">
								{mutation.isPending ? (
									<Spinner className="text-white" />
								) : (
									"ບັນທຶກ"
								)}
							</Button>
						</div>
					</form>
				</CardContent>
			</Card>
		</>
	);
}

export default RouteComponent;
